import type { Turn } from '../../types/runExplorer';
import { formatClock, formatMs } from './runExplorerShared';

/**
 * One card per turn of a threaded run (task 1151, step 6). Selecting a card
 * scopes the path, the timeline and the journal to that turn. A single-shot
 * run has one turn and the page renders no strip.
 */
export function TurnStrip({
  turns,
  selectedTurnIndex,
  onSelect,
}: {
  turns: Turn[];
  selectedTurnIndex: number;
  onSelect: (index: number) => void;
}) {
  if (turns.length <= 1) return null;
  return (
    <div className="flex gap-3 overflow-x-auto pb-1" role="tablist" aria-label="Turns" data-testid="turn-strip">
      {turns.map((turn) => {
        const active = turn.index === selectedTurnIndex;
        const message = turn.userMessage ?? '';
        const counts = [
          `${turn.modelCallCount} model ${turn.modelCallCount === 1 ? 'call' : 'calls'}`,
          turn.toolCallCount > 0 ? `${turn.toolCallCount} ${turn.toolCallCount === 1 ? 'tool' : 'tools'}` : null,
          turn.childRuns.length > 0 ? `${turn.childRuns.length} child ${turn.childRuns.length === 1 ? 'run' : 'runs'}` : null,
          formatMs(turn.durationMs),
        ].filter(Boolean);
        return (
          <button
            key={turn.index}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`turn-card-${turn.index}`}
            onClick={() => onSelect(turn.index)}
            className={`flex-shrink-0 w-64 text-left rounded-lg border p-3 transition-colors ${
              active
                ? 'border-indigo-500 ring-2 ring-indigo-200 dark:ring-indigo-800 bg-indigo-50 dark:bg-indigo-900/30'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                Turn {turn.index + 1} · {formatClock(turn.startedAt)}
              </span>
              <span
                className={`px-1.5 py-0.5 rounded font-medium truncate max-w-[9rem] ${
                  turn.outcome.problem
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                }`}
                title={turn.outcome.summary}
              >
                {turn.outcome.summary}
              </span>
            </div>
            <p className="mt-1.5 text-sm text-gray-900 dark:text-white line-clamp-2" title={message}>
              {message ? `“${message.length > 90 ? `${message.slice(0, 90)}…` : message}”` : 'No message recorded'}
            </p>
            {turn.specialists.length > 0 && (
              <p className="mt-1 text-xs font-mono text-gray-600 dark:text-gray-300 truncate">
                {turn.specialists.join(' → ')}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{counts.join(' · ')}</p>
          </button>
        );
      })}
    </div>
  );
}
